import React, { useState, useEffect } from "react";
import { FlaskConical, ArrowUpRight } from "lucide-react";
import Reveal, { Eyebrow } from "../components/common/Reveal";
import { evidence, BOOK_URL } from "../mock";

export default function EvidenceLibrary() {
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const shown =
    filter === "All"
      ? evidence.studies
      : evidence.studies.filter((s) => s.category === filter);

  return (
    <main>
      {/* Hero */}
      <section className="pt-28 lg:pt-36 pb-14 bg-gradient-to-b from-lybl-sand to-lybl-paper">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <Reveal><Eyebrow color="blue">{evidence.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={80}><h1 className="mt-4 font-serif text-4xl lg:text-6xl text-lybl-ink text-balance">{evidence.title}</h1></Reveal>
          <Reveal delay={140}><p className="mt-5 text-lg text-lybl-muted max-w-2xl mx-auto">{evidence.subtitle}</p></Reveal>
        </div>
      </section>

      {/* Filters + grid */}
      <section className="bg-lybl-paper pb-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div className="flex flex-wrap justify-center gap-2 mb-12">
              {evidence.filters.map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`rounded-full px-4 py-2 text-[13px] font-medium transition-colors ${
                    filter === f
                      ? "bg-lybl-green text-white"
                      : "bg-white text-lybl-ink/70 ring-1 ring-black/5 hover:bg-lybl-cream"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </Reveal>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {shown.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 70}>
                <article className="group h-full flex flex-col overflow-hidden rounded-3xl bg-white ring-1 ring-black/5 hover:shadow-xl transition-shadow">
                  <div className="h-40 overflow-hidden">
                    <img src={s.image} alt="" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-2 text-[12px]">
                      <span className="rounded-full bg-lybl-lavender px-2.5 py-1 font-medium text-lybl-blue">{s.category}</span>
                      <span className="text-lybl-muted">{s.type}</span>
                    </div>
                    <h3 className="mt-3 font-serif text-lg text-lybl-ink leading-snug">{s.title}</h3>
                    <div className="mt-4 flex items-start gap-2 rounded-xl bg-lybl-cream/60 p-3">
                      <FlaskConical className="h-4 w-4 text-lybl-green mt-0.5 shrink-0" />
                      <p className="text-[13px] text-lybl-ink/80">{s.result}</p>
                    </div>
                    <p className="mt-4 text-[12px] text-lybl-muted">{s.meta}</p>
                    <button className="mt-4 inline-flex items-center gap-1 text-[14px] font-medium text-lybl-rust self-start">
                      Read summary <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          {shown.length === 0 && (
            <p className="text-center text-lybl-muted py-16">No studies in this category yet.</p>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-lybl-green py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 text-center">
          <h2 className="font-serif text-3xl lg:text-4xl text-white text-balance">Care built on evidence, personalized to you</h2>
          <p className="mt-4 text-white/75 text-lg">Start with a free consultation and see how our research-backed protocols fit your health.</p>
          <a href={BOOK_URL} target="_blank" rel="noreferrer" data-evt="schedule" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-4 text-[15px] font-medium text-lybl-green hover:bg-lybl-cream transition-colors">
            Book Your Consult <span aria-hidden>&rarr;</span>
          </a>
        </div>
      </section>
    </main>
  );
}
